import { READY_CHECK_MS } from '../../shared/config';
import {
  queueServerMessageSchema,
  type QueueClientMessage,
  type QueueRefusal,
  type QueueServerMessage,
} from '../../shared/matchmaking-protocol';
import { CLOSE_CODES } from '../../shared/protocol';
import { QUEUE_SOCKET_PATH } from '../../shared/routes';
import { socketUrl } from '../platform/socket-url';

export interface QueueOpponent {
  name: string;
  avatar: string;
  bot: boolean;
}

/** What the Competitive screen shows; times are local milliseconds. */
export type QueueView =
  | { kind: 'idle'; notice: string | null }
  | { kind: 'connecting' }
  | { kind: 'searching'; since: number }
  | { kind: 'proposal'; id: string; endAt: number; opponent: QueueOpponent; accepted: boolean }
  | { kind: 'refused'; reason: QueueRefusal; message: string; roomCode: string | null }
  | { kind: 'lost' };

export interface QueueEvents {
  /** Resolves the profile credential, creating the profile if needed. */
  credential(): Promise<string>;
  matched(roomCode: string): void;
}

type SocketFactory = (url: string) => WebSocket;

/**
 * One Competitive queue attempt over the Matchmaker socket. The queue position lives on the
 * server with the socket, so a dropped connection ends the attempt rather than reconnecting.
 */
export class QueueConnection {
  view = $state<QueueView>({ kind: 'idle', notice: null });
  private socket: WebSocket | null = null;
  private searchStartedAt = 0;

  constructor(
    private readonly events: QueueEvents,
    private readonly createSocket: SocketFactory = (url) => new WebSocket(url),
  ) {}

  async start(): Promise<void> {
    if (this.socket) return;
    this.view = { kind: 'connecting' };
    let credential: string;
    try {
      credential = await this.events.credential();
    } catch {
      this.view = { kind: 'lost' };
      return;
    }
    // Cancelled while the profile was loading.
    if (this.view.kind !== 'connecting') return;
    const socket = this.createSocket(socketUrl(QUEUE_SOCKET_PATH));
    this.socket = socket;
    this.searchStartedAt = Date.now();
    socket.onopen = () => this.send({ t: 'enqueue', profileCredential: credential });
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      let decoded: unknown;
      try {
        decoded = JSON.parse(String(event.data));
      } catch {
        return;
      }
      const parsed = queueServerMessageSchema.safeParse(decoded);
      if (parsed.success) this.receive(parsed.data);
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.view.kind === 'searching' || this.view.kind === 'proposal' || this.view.kind === 'connecting')
        this.view = { kind: 'lost' };
    };
  }

  cancel(): void {
    this.send({ t: 'cancel' });
    this.close({ kind: 'idle', notice: null });
  }

  accept(): void {
    if (this.view.kind !== 'proposal' || this.view.accepted) return;
    this.send({ t: 'accept', proposalId: this.view.id });
    this.view = { ...this.view, accepted: true };
  }

  decline(): void {
    if (this.view.kind !== 'proposal') return;
    this.send({ t: 'decline', proposalId: this.view.id });
    this.close({ kind: 'idle', notice: null });
  }

  dispose(): void {
    if (this.socket) this.send({ t: 'cancel' });
    this.close({ kind: 'idle', notice: null });
  }

  private receive(message: QueueServerMessage): void {
    switch (message.t) {
      case 'queued':
        this.view = { kind: 'searching', since: this.searchStartedAt };
        break;
      case 'proposal':
        // The server opened the proposal just now; a local deadline avoids clock skew.
        this.view = {
          kind: 'proposal',
          id: message.id,
          endAt: Date.now() + READY_CHECK_MS,
          opponent: message.opponent,
          accepted: false,
        };
        break;
      case 'matched':
        this.close({ kind: 'idle', notice: null });
        this.events.matched(message.roomCode);
        break;
      case 'left':
        this.close({ kind: 'idle', notice: 'You left the queue.' });
        break;
      case 'refused':
        this.close({
          kind: 'refused',
          reason: message.reason,
          message: message.msg,
          roomCode: message.roomCode ?? null,
        });
        break;
    }
  }

  private send(message: QueueClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private close(view: QueueView): void {
    const socket = this.socket;
    this.socket = null;
    this.view = view;
    socket?.close(CLOSE_CODES.normal);
  }
}
